import { useEffect, useRef, useState } from 'react';
import { ButtonProps } from '@mui/material/Button';
import { ConfirmDialog } from '../../shared/ConfirmDialog';

const CONFIRM_NOTIFICATION_EVENT_NAME = 'zenput:confirmNotification';

interface ConfirmNotificationConfig {
    message: string;
    success: (status: boolean) => void;
    title: string;
    yes: string;
    no: string;
    confirmProps: ButtonProps;
}

type ConfirmEvent = CustomEvent<{
    message: ConfirmNotificationConfig['message'];
    success: ConfirmNotificationConfig['success'];
    title: ConfirmNotificationConfig['title'];
    yes: ConfirmNotificationConfig['yes'];
    no: ConfirmNotificationConfig['no'];
    confirmProps: ButtonProps;
}>;

const listenToConfirm = (listener: (event: ConfirmEvent) => void) => {
    document.addEventListener(CONFIRM_NOTIFICATION_EVENT_NAME, listener);
};

const removeConfirmListener = (listener: (event: ConfirmEvent) => void) => {
    document.removeEventListener(CONFIRM_NOTIFICATION_EVENT_NAME, listener);
};

export const showConfirmNotification = (
    message: string,
    success: (status: boolean) => void,
    title: string,
    yes: string,
    no: string,
    confirmProps: ButtonProps
) => {
    const event: ConfirmEvent = new CustomEvent(CONFIRM_NOTIFICATION_EVENT_NAME, {
        detail: { message, success, title, yes, no, confirmProps }
    });

    document.dispatchEvent(event);
};

export const ConfirmNotification = () => {
    const [showDialog, setShowDialog] = useState(false);
    const notificationConfig = useRef<ConfirmNotificationConfig>({
        message: '',
        success: (_: boolean) => {},
        title: '',
        yes: '',
        no: '',
        confirmProps: {}
    });

    useEffect(() => {
        const listener = (event: ConfirmEvent) => {
            notificationConfig.current = event.detail;
            setShowDialog(true);
        };

        listenToConfirm(listener);

        return () => {
            removeConfirmListener(listener);
        };
    }, []);

    const onClose = () => {
        setShowDialog(false);
    };

    return (
        <ConfirmDialog
            open={showDialog}
            title={notificationConfig.current.title}
            description={notificationConfig.current.message}
            confirmLabel={notificationConfig.current.yes}
            cancelLabel={notificationConfig.current.no}
            confirmProps={notificationConfig.current.confirmProps}
            onConfirm={() => {
                onClose();
                notificationConfig.current.success(true);
            }}
            onCancel={() => {
                onClose();
                notificationConfig.current.success(false);
            }}
            onClose={onClose}
        />
    );
};
